import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";

// Reusable ICD-10 code search backed by the icd10_codes Supabase table.
// Multi-select by default; pass `singleSelect` for a primary-diagnosis picker.
// Selected value shape: { code, description, category }
//
// Multi-select returns an array via `onChange`; single-select returns one
// object (or null).
const Icd10Search = ({
  value,
  onChange,
  singleSelect = false,
  placeholder = "Search ICD-10 code or description...",
  label,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  // Normalize value to an array internally.
  const selectedList = singleSelect
    ? (value ? [value] : [])
    : (Array.isArray(value) ? value : []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const q = query.trim();
      const { data, error } = await supabase
        .from("icd10_codes")
        .select("code, description, category")
        .or(`code.ilike.%${q}%,description.ilike.%${q}%`)
        .order("category", { ascending: true })
        .order("code", { ascending: true })
        .limit(20);
      if (!error) setResults(data || []);
      setLoading(false);
      setSearched(true);
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  // Click-outside to close dropdown.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const isSelected = (code) =>
    selectedList.some((s) => s && s.code === code);

  const choose = (item) => {
    if (singleSelect) {
      onChange?.(item);
    } else {
      if (isSelected(item.code)) return;
      onChange?.([...selectedList, item]);
    }
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const removeAt = (idx) => {
    if (singleSelect) {
      onChange?.(null);
    } else {
      const next = selectedList.filter((_, i) => i !== idx);
      onChange?.(next);
    }
  };

  // Add a free-text code when the user can't find a match.
  const addManual = () => {
    const raw = query.trim().toUpperCase();
    if (!raw) return;
    const item = { code: raw, description: "(manual entry)", category: "Custom" };
    choose(item);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      {label && (
        <label style={{ display: "block", fontSize: 11, fontWeight: 700,
          color: "#4B5563", marginBottom: 5, textTransform: "uppercase",
          letterSpacing: "0.06em" }}>
          {label}
        </label>
      )}

      {/* Selected chips */}
      {selectedList.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6,
          marginBottom: 6 }}>
          {selectedList.map((s, i) => (
            <span key={`${s.code}-${i}`} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#EEF2FF", color: "#4338CA",
              border: "1px solid #C7D2FE", borderRadius: 16,
              padding: "3px 10px", fontSize: 12, fontWeight: 500,
            }}>
              <strong style={{ fontSize: 11 }}>{s.code}</strong>
              <span style={{ color: "#4B5563" }}>{s.description}</span>
              <button type="button" onClick={() => removeAt(i)}
                aria-label="Remove" style={{
                  background: "none", border: "none", color: "#4338CA",
                  cursor: "pointer", padding: 0, fontSize: 14,
                  lineHeight: 1,
                }}>×</button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      {(!singleSelect || selectedList.length === 0) && (
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && results.length === 0 && query.trim()) {
              e.preventDefault();
              addManual();
            }
          }}
          placeholder={placeholder}
          style={{
            width: "100%", padding: "10px 14px",
            border: "1.5px solid #E5E7EB", borderRadius: 8,
            fontSize: 14, color: "#1F2937",
            fontFamily: "DM Sans, sans-serif",
            background: "#fff", boxSizing: "border-box", outline: "none",
          }}
        />
      )}

      {/* Dropdown */}
      {open && query.trim().length >= 2 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: "#fff",
          border: "1.5px solid #E5E7EB", borderRadius: 8,
          marginTop: 4, maxHeight: 320, overflowY: "auto", zIndex: 30,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        }}>
          {loading && (
            <div style={{ padding: "10px 12px", fontSize: 12,
              color: "#6B7280" }}>Searching…</div>
          )}
          {!loading && results.length > 0 && results.map((r) => (
            <div key={r.code}
              onClick={() => choose(r)}
              style={{
                padding: "8px 12px", cursor: "pointer",
                borderBottom: "1px solid #F3F4F6",
                display: "flex", alignItems: "center", gap: 10,
                opacity: isSelected(r.code) ? 0.45 : 1,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
            >
              <strong style={{ fontSize: 12, color: "#6B2D9E",
                minWidth: 80 }}>{r.code}</strong>
              <span style={{ flex: 1, fontSize: 13,
                color: "#1F2937" }}>{r.description}</span>
              {r.category && (
                <span style={{ fontSize: 10, fontWeight: 600,
                  background: "#FEF3C7", color: "#92400E",
                  padding: "2px 8px", borderRadius: 10 }}>
                  {r.category}
                </span>
              )}
            </div>
          ))}
          {!loading && searched && results.length === 0 && (
            <div style={{ padding: "10px 12px", fontSize: 12,
              color: "#6B7280" }}>
              No matches.
              <button type="button" onClick={addManual}
                style={{ marginLeft: 8, background: "#6B2D9E",
                  color: "#fff", border: "none", borderRadius: 6,
                  padding: "3px 10px", fontSize: 12, cursor: "pointer" }}>
                + Add "{query.trim().toUpperCase()}" as manual entry
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Icd10Search;
